import { api } from './client'
import type {
    PaymentMethodChange,
    PaymentMethodChangeTarget,
} from './sales-order-api'

// Ledger cấp khách (KiotViet-style). Namespace API mới, tách khỏi
// customer_debts per-đơn cũ. Xem docs/.../2026-08-05-customer-debt-ledger-design.md

export type DebtTransactionType =
    | 'sale'
    | 'payment'
    | 'adjustment'
    | 'return'
    | 'opening'
    | 'refund'
    | 'return_fee'

// Phương thức thu của phiếu thu cấp khách. `card` là mới so với luồng cũ
// (cash / bank_transfer).
export type ReceiptPaymentMethod = 'cash' | 'bank_transfer' | 'card'

// Một dòng sổ cái công nợ: `amount` là delta CÓ DẤU, `balance_after` là số dư
// luỹ kế sau dòng này. Danh sách sổ trả về theo ngày thu (`occurred_at`) mới
// nhất trước, và `balance_after` cộng dồn theo đúng thứ tự ngày đó.
export interface DebtTransaction {
    id: string
    customer_id: string
    type: DebtTransactionType
    amount: number
    balance_after: number
    reference_code?: string
    sales_order_id?: string
    customer_payment_id?: string
    adjustment_id?: string
    receipt_id?: string
    occurred_at: string
    note?: string
    created_by_id?: string
    created_at: string
}

export interface DebtTransactionsParams {
    type?: DebtTransactionType
    from?: string
    to?: string
    page?: number
    per_page?: number
}

// Phân bổ 1 phần tiền phiếu thu vào 1 đơn còn nợ.
export interface DebtAllocation {
    sales_order_id: string
    amount: number
}

export interface CreatePaymentReceiptRequest {
    amount: number
    payment_method: ReceiptPaymentMethod
    occurred_at?: string
    note?: string
    // Bỏ trống → server tự phân bổ FIFO (cũ→mới); dư → credit (nợ âm).
    allocations?: DebtAllocation[]
    // Bỏ qua cảnh báo phiếu thu trùng (server 409 duplicate_receipt) sau khi
    // người dùng xác nhận "vẫn tạo".
    force?: boolean
}

// Phiếu cũ đính kèm trong lỗi 409 duplicate_receipt (error.details.existing).
export interface DuplicateReceiptInfo {
    id: string
    amount: number
    occurred_at: string
    note?: string
    created_at: string
}

export interface PaymentReceiptResponse {
    balance_after: number
    receipt: {
        id: string
        customer_id: string
        amount: number
        payment_method: ReceiptPaymentMethod
        occurred_at?: string
        note?: string
        created_at: string
    }
}

// Điều chỉnh dạng set-target: server tính delta = target_balance − current.
export interface CreateDebtAdjustmentRequest {
    target_balance: number
    occurred_at?: string
    note?: string
}

export interface DebtAdjustmentResponse {
    balance_after: number
    adjustment: {
        id: string
        customer_id: string
        balance_before: number
        target_balance: number
        delta: number
        occurred_at?: string
        note?: string
        created_at: string
    }
}

// 1 đơn mà phiếu thu đã phân bổ vào (cho popup chi tiết chứng từ).
export interface ReceiptAllocation {
    sales_order_id?: string
    order_code: string
    amount: number
    payment_method: string
}

export interface ReceiptDetail {
    receipt: {
        id: string
        customer_id: string
        amount: number
        payment_method: ReceiptPaymentMethod
        collector_user_id?: string
        collector_name?: string
        occurred_at: string
        note?: string
        created_at: string
        /** Các lần đổi hình thức cả phiếu (M-152), cũ nhất trước. */
        method_changes?: PaymentMethodChange[]
    }
    allocations: ReceiptAllocation[]
}

export interface AllocateCreditRequest {
    allocations?: DebtAllocation[]
}

export interface AllocateCreditResponse {
    allocated: number
}

export const customerDebtApi = {
    listTransactions: (id: string, params?: DebtTransactionsParams) =>
        api.get<DebtTransaction[]>(`/customers/${id}/debt-transactions`, {
            params,
        }),

    // Xuất sổ công nợ ra Excel. Nhận CÙNG bộ lọc với listTransactions (trừ
    // phân trang — file luôn xuất trọn bộ lọc) để file khớp đúng cái đang xem.
    // Dùng getBlob thay vì window.open: đi qua interceptor refresh 401, hết
    // phiên vẫn tải được thay vì ra file hỏng.
    exportTransactions: (
        id: string,
        params?: Omit<DebtTransactionsParams, 'page' | 'per_page'>,
    ) => api.getBlob(`/customers/${id}/debt-transactions/export`, { params }),

    createPaymentReceipt: (id: string, data: CreatePaymentReceiptRequest) =>
        api.post<PaymentReceiptResponse>(
            `/customers/${id}/payment-receipts`,
            data,
        ),

    getPaymentReceipt: (id: string, receiptId: string) =>
        api.get<ReceiptDetail>(
            `/customers/${id}/payment-receipts/${receiptId}`,
        ),

    /** Đổi hình thức cả phiếu thu + mọi khoản phân bổ (M-152). */
    changePaymentReceiptMethod: (
        id: string,
        receiptId: string,
        method: PaymentMethodChangeTarget,
    ) =>
        api.patch<ReceiptDetail['receipt']>(
            `/customers/${id}/payment-receipts/${receiptId}/method`,
            { method },
        ),

    createAdjustment: (id: string, data: CreateDebtAdjustmentRequest) =>
        api.post<DebtAdjustmentResponse>(
            `/customers/${id}/debt-adjustments`,
            data,
        ),

    allocateCredit: (id: string, data: AllocateCreditRequest) =>
        api.post<AllocateCreditResponse>(
            `/customers/${id}/allocate-credit`,
            data,
        ),

    // Xóa cứng 1 dòng công nợ nhân viên tạo (điều chỉnh / phiếu thu / thanh
    // toán theo đơn nhập tay). Server đảo bút toán liên quan + tính lại số dư;
    // dòng sinh từ đơn hàng / luồng tự động bị chặn 409.
    deleteTransaction: (id: string, txnId: string, reason?: string) =>
        api.delete<{ balance_after: number }>(
            `/customers/${id}/debt-transactions/${txnId}`,
            reason ? { data: { reason } } : undefined,
        ),
}
